import { queryKeys } from '@query/keys'
import { PaginatedQueryResult, usePaginatedQuery } from '@query/usePaginatedQuery'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageQuery, SortOrder, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'
import {
  IBRebateApprovalSortKeysDto,
  IntroducingBrokerRebateApprovalDto,
} from '@model/RebateApprovalsDto'

export const defaultApprovalsSort: { sort: string; sortOrder: SortOrder }[] = [
  { sort: 'statusPriority', sortOrder: 'ASC' },
  { sort: 'createdDate', sortOrder: 'DESC' },
]

interface IBRebateApprovals {
  query: PaginatedQueryResult<IntroducingBrokerRebateApprovalDto>
  tradingAccountOptions: NameDto<string>[]
  sortOptions: NameDto<IBRebateApprovalSortKeysDto | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  forceRefresh: () => void
  clearSearch: (callback: () => void) => void
}

interface IBRebateApprovalsParams {
  onCompleted?: () => void
  overrides?: PageQuery
}

export const useIBRebateApprovals = (props: IBRebateApprovalsParams = {}): IBRebateApprovals => {
  const { onCompleted, overrides } = props
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<IBRebateApprovalSortKeysDto | null>[] = [
    { id: 'tradingAccountName', name: t('Trading Account.Trading Account') },
    { id: 'accountType', name: t('Account Type') },
    { id: 'groupName', name: t('IB.RebatesPage.ClientsPage.Group') },
    { id: 'accumulatedVolumeFxm', name: t('IB.RebatesPage.ClientsPage.Vol. FXM') },
    { id: 'accumulatedVolumeOther', name: t('IB.RebatesPage.ClientsPage.Vol. Other') },
    { id: 'accumulatedCommission', name: t('Commission') },
    { id: 'rebateAmount', name: t('IB.RebatesPage.Rebates') },
    { id: 'rebatePayoutStatusId', name: t('Status') },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const ibId = account?.clientIntroducingBroker?.id

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.rebates.approvals(ibId, { ...overrides, search, locale } as any),
    queryFn: async (pageQuery) => {
      const res = await apiClient.getIBRebateApprovals(ibId ?? '', {
        ...pageQuery,
        ...overrides,
        search,
        caller: 'ca',
        languageId: locale,
        dismissNotifications: true,
        sort: pageQuery?.sort ?? defaultApprovalsSort,
      })

      const hasNotification = res.items.some(({ showNotification }) => showNotification)

      if (hasNotification) {
        onCompleted?.()
      }

      return res
    },
  })

  const tradingAccountOptions = useMemo(() => {
    return query.data?.map((item) => item.tradingAccount) || []
  }, [query.data])

  return {
    query,
    tradingAccountOptions,
    sortOptions,
    search,
    setSearch,
    forceRefresh: query.forceRefresh,
    clearSearch,
  }
}
